/**
 * The Stat Dictionary.
 *
 * Shared across every source, which is the point: Direction is a property of
 * the statistic, not of whoever publishes it, so correcting an entry here
 * corrects every profile bound to it at once.
 */

import { memo, useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  api,
  type Direction,
  type StatEntry,
} from "../api";
import { keys } from "../queries";
import { Layout } from "../components/Layout";
import {
  Badge,
  Banner,
  Button,
  Card,
  Code,
  EmptyState,
  Field,
  FieldSet,
  Modal,
  Muted,
  SelectCompact,
  Select,
  Spinner,
  TableScroll,
  TextArea,
  TextInput,
  TextInputCompact,
} from "../components/ui";
import {
  CloseIcon,
  DictionaryIcon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  SortIcon,
  TrashIcon,
} from "../components/icons";
import { parseWeightPercent } from "../stat-weight";
import {
  exclusionHint,
  labelsFor,
  optionsFor,
  sameGroups,
  type PositionGroups,
} from "../position-exclusions";
import { PositionGroupPicker } from "../components/PositionGroupPicker";
import {
  DEFAULT_VIEW,
  isNarrowed,
  viewStats,
  type StatColumn,
  type StatView,
} from "../stat-dictionary-view";
import {
  type StatDraft,
  type StatPatch,
  changesFor,
  draftFor,
  renameSummary,
} from "../stat-edit";

/**
 * The sports a statistic may be filed under.
 *
 * The naming standard's §7 list, which is the same set `app/domain/naming.py`
 * validates a sport folder against. Kept in step by hand because it is a
 * published standard rather than a moving configuration — gender and division
 * belong in the league name, never here.
 */
const SPORTS = ["baseball", "basketball", "football", "hockey", "lacrosse", "soccer"];

export function StatDictionary() {
  const queryClient = useQueryClient();
  const [key, setKey] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [direction, setDirection] = useState<Direction>("higher_is_better");
  const [aliases, setAliases] = useState("");
  /**
   * The default Weight box as typed, not as a number.
   *
   * Text for the same reason the Fields screen's cell is: parsing each keystroke
   * into a number fights a half-typed "0.". Parsed on submit, and blank stays
   * blank — a statistic that declares no weight is the normal case and must not
   * be turned into a declared 1.
   */
  const [weight, setWeight] = useState("");
  /** What is wrong with the Weight box, or null. */
  const [weightProblem, setWeightProblem] = useState<string | null>(null);
  // Which sport a new entry is filed under, and which the table is showing.
  // Two separate things on purpose: adding a soccer statistic while looking at
  // the football list is a reasonable thing to do, and a single control that
  // did both would make the add form change what it writes when somebody
  // filtered the table to read something.
  const [sport, setSport] = useState("football");
  /**
   * Which Canonical Position Groups a new statistic will not rank.
   *
   * Reset after a successful add, like the key and the aliases: statistics are
   * typed into this form in batches, and a selection that persisted would
   * silently exclude the same positions from the next forty.
   */
  const [excluded, setExcluded] = useState<string[]>([]);
  const [view, setView] = useState<StatView>(DEFAULT_VIEW);
  const narrow = (change: Partial<StatView>) => setView((current) => ({ ...current, ...change }));
  /**
   * Which row is open in the editor, by id rather than by value.
   *
   * By id so the open editor follows the row through a refetch: holding the
   * entry itself would leave the form showing a stale copy of a row somebody
   * corrected in another tab, and the thing being edited here is read by every
   * source in the sport.
   */
  const [editing, setEditing] = useState<string | null>(null);

  const { data: entries, error: loadError } = useQuery({
    queryKey: keys.stats,
    queryFn: () => api.listStats(),
  });

  const { data: sports } = useQuery({
    queryKey: ["stat-dictionary-sports"],
    queryFn: api.listStatSports,
  });

  const { data: vocabularies } = useQuery({
    queryKey: ["vocabularies"],
    queryFn: api.listVocabularies,
  });

  /**
   * The Canonical Position Groups each sport offers, for the *Positions Groups
   * Excluded* control.
   *
   * One request for the whole table, because the options are a property of the
   * sport rather than of the row — and this table renders 488 rows. Not
   * invalidated by a write to the dictionary: adding a statistic cannot change
   * which positions a sport has.
   */
  const { data: positionGroups } = useQuery({
    queryKey: keys.positionGroups,
    queryFn: api.listPositionGroups,
  });

  /**
   * Every write here invalidates the same key, because every write changes the
   * same list. Sharing one settled-callback keeps that true as mutations are
   * added — the alternative is remembering to invalidate in three places.
   */
  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: keys.stats });
    await queryClient.invalidateQueries({ queryKey: ["vocabularies"] });
    // The sport list is derived from the entries, so a first entry in a new
    // sport adds a filter option. Invalidated together or the option appears
    // only after a reload.
    await queryClient.invalidateQueries({ queryKey: ["stat-dictionary-sports"] });
    // And what every entry is used by. A rename carries Bindings across, so the
    // counts the editor warned about are stale the moment it saves — and a
    // second open showing the pre-rename figures would be describing a cascade
    // that has already happened.
    await queryClient.invalidateQueries({ queryKey: ["stat-usage"] });
  };

  const create = useMutation({
    mutationFn: () => {
      const parsed = parseWeightPercent(weight);
      return api.createStat({
        sport,
        key,
        display_name: displayName,
        direction,
        // Omitted rather than sent as null when nothing was typed, so the row
        // records "nothing declared" and not a decision nobody made.
        ...(parsed.ok && parsed.weight != null ? { default_weight: parsed.weight } : {}),
        aliases: aliases.split(",").map((a) => a.trim()).filter(Boolean),
        // Omitted rather than sent empty where nothing was chosen, which is the
        // same thing to the server and keeps the body to what was decided.
        ...(excluded.length > 0 ? { excluded_position_groups: excluded } : {}),
      });
    },
    onSuccess: async () => {
      setKey("");
      setDisplayName("");
      setAliases("");
      setWeight("");
      setExcluded([]);
      await invalidate();
    },
  });

  const installVocabulary = useMutation({
    mutationFn: (key: string) => api.installVocabulary(key),
    onSuccess: invalidate,
  });

  const changeDirection = useMutation({
    mutationFn: ({ entry, next }: { entry: StatEntry; next: Direction }) =>
      api.updateStat(entry.id, { direction: next }),
    onSuccess: invalidate,
  });

  const changeWeight = useMutation({
    mutationFn: ({ entry, next }: { entry: StatEntry; next: number | null }) =>
      api.updateStat(entry.id, { default_weight: next }),
    onSuccess: invalidate,
  });

  /**
   * The one write on this screen that re-ranks sources already reviewed.
   *
   * Direction beside it does too, and the screen's description says so for both.
   * A Weight does not — it only seeds a Binding that does not exist yet — which
   * is the asymmetry that keeps this screen clear of ADR-0040.
   */
  const changeExclusions = useMutation({
    mutationFn: ({ entry, next }: { entry: StatEntry; next: string[] }) =>
      api.updateStat(entry.id, { excluded_position_groups: next }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (entry: StatEntry) => api.deleteStat(entry.id),
    onSuccess: invalidate,
  });

  const error =
    messageFor(loadError, "Could not load the dictionary.") ??
    messageFor(create.error, "Could not add that statistic.") ??
    messageFor(changeDirection.error, "Could not change that Direction.") ??
    messageFor(changeWeight.error, "Could not change that Weight.") ??
    messageFor(
      changeExclusions.error,
      "Could not change which positions that statistic excludes.",
    ) ??
    messageFor(remove.error, "Could not remove that statistic.") ??
    messageFor(installVocabulary.error, "Could not install that vocabulary.");

  // Deferred so the search box and the Clear button answer at once and the
  // table catches up a frame later, rather than the click waiting on the table.
  const deferredView = useDeferredValue(view);
  const settling = deferredView !== view;
  // Every entry in sort order, and the ids the search and filters keep. Rows are
  // hidden rather than dropped — see `StatRow`.
  const ordered = useMemo(
    () => viewStats(entries ?? [], { ...DEFAULT_VIEW, sort: deferredView.sort }),
    [entries, deferredView.sort],
  );
  const shown = useMemo(
    () => viewStats(entries ?? [], deferredView),
    [entries, deferredView],
  );
  const visible = useMemo(() => new Set(shown.map((entry) => entry.id)), [shown]);

  const { mutate: mutateDirection } = changeDirection;
  const { mutate: mutateWeight } = changeWeight;
  const { mutate: mutateExclusions } = changeExclusions;
  const { mutate: mutateRemove } = remove;
  const onDirection = useCallback(
    (entry: StatEntry, next: Direction) => mutateDirection({ entry, next }),
    [mutateDirection],
  );
  const onWeight = useCallback(
    (entry: StatEntry, next: number | null) => mutateWeight({ entry, next }),
    [mutateWeight],
  );
  const onExclusions = useCallback(
    (entry: StatEntry, next: string[]) => mutateExclusions({ entry, next }),
    [mutateExclusions],
  );
  const onRemove = useCallback((entry: StatEntry) => mutateRemove(entry), [mutateRemove]);

  if (!entries) {
    return (
      <Layout breadcrumbs={[{ label: "Stat Dictionary" }]}>
        <Spinner label="Loading the dictionary" />
      </Layout>
    );
  }

  /** A column header that sorts by its column, and reverses on a second click. */
  const sortable = (column: StatColumn, label: string) => {
    const active = view.sort.column === column;
    return (
      <th aria-sort={active ? (view.sort.descending ? "descending" : "ascending") : "none"}>
        <button
          type="button"
          className="th-sort"
          title={active ? `Sort by ${label}, the other way` : `Sort by ${label}`}
          onClick={() =>
            narrow({ sort: { column, descending: active ? !view.sort.descending : false } })
          }
        >
          {label}
          <SortIcon direction={active ? (view.sort.descending ? "desc" : "asc") : null} />
        </button>
      </th>
    );
  };

  return (
    <Layout
      breadcrumbs={[{ label: "Stat Dictionary" }]}
      title="Stat Dictionary"
      // **The two columns are not the same kind of thing, and the description
      // has to say so** — they sit side by side and look alike. A Direction
      // edited here changes every source bound to the statistic, immediately. A
      // Weight edited here changes nothing that already exists: it is what the
      // *next* Binding starts from, and a source already reviewed keeps the
      // weight on its own Bindings. That asymmetry is what keeps this screen
      // clear of ADR-0040, which refused a weight on the entry that the ranker
      // read.
      description="Changing a Direction here changes it for every source bound to that statistic, because whether a high number is good is a property of the statistic rather than of the source. A Weight works the other way round: it is only what a new Binding starts from, so changing it never re-ranks a source already reviewed — adjust those on that source's own Fields screen."
      wide
    >
      <div className="flex flex-col gap-lg">
        {error && <Banner tone="danger" title={error} />}

        {/*
          Offered above the add form on purpose. Somebody arriving at an empty
          dictionary should meet "install ninety-two entries" before "type one
          in", which is the whole of the client's complaint about filling this
          in by hand.
        */}
        {(vocabularies ?? []).length > 0 && (
          <Card
            title="Vocabularies"
            description="A sport's statistics, pre-authored with their Directions and the aliases that auto-bind during discovery. Installing is additive — it never changes an entry you have edited."
          >
            <div className="flex flex-col gap-sm">
              {(vocabularies ?? []).map((vocabulary) => {
                const complete = vocabulary.installed_count >= vocabulary.entry_count;
                return (
                  <div
                    key={vocabulary.key}
                    className="flex flex-wrap items-center justify-between gap-sm"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-xs">
                        <strong className="text-body-sm-medium">{vocabulary.name}</strong>
                        <Badge tone={complete ? "success" : undefined}>
                          {vocabulary.installed_count} of {vocabulary.entry_count} installed
                        </Badge>
                      </div>
                      <Muted>{vocabulary.description}</Muted>
                    </div>
                    <Button
                      variant={complete ? "secondary" : "primary"}
                      disabled={complete || installVocabulary.isPending}
                      onClick={() => installVocabulary.mutate(vocabulary.key)}
                    >
                      {complete
                        ? "Installed"
                        : installVocabulary.isPending
                          ? "Installing…"
                          : "Install"}
                    </Button>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        <Card title="Add a statistic">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              // Refused at the form, which is where ADR-0040 puts the refusal at
              // nought. Checked before the mutation rather than inside it so a
              // bad weight never reaches the server as a 422 the form could have
              // explained itself.
              const parsed = parseWeightPercent(weight);
              if (!parsed.ok) {
                setWeightProblem(parsed.message);
                return;
              }
              setWeightProblem(null);
              create.mutate();
            }}
          >
            <div className="flex flex-col gap-md">
              <div className="grid grid-cols-1 gap-md fold:grid-cols-4">
                {/*
                  An entry is identified by sport and key together (ADR-0017),
                  so the sport is part of adding one rather than a property set
                  afterwards. Whether a high number is good is a property of the
                  statistic *in a sport*: `penalties` is penalty flags in
                  football and penalty kicks in soccer, and the Direction flips.
                */}
                <Field label="Sport" hint="Its Direction applies within this sport only.">
                  {/*
                    Changing the sport clears the exclusions. The position groups
                    are one sport's — football has no `GK`, and the server
                    refuses a football statistic that excludes one — so a
                    selection carried across sports is a selection the Add
                    button would be refused for, with the control showing a
                    value that is no longer on offer.
                  */}
                  <Select
                    value={sport}
                    onChange={(e) => {
                      setSport(e.target.value);
                      setExcluded([]);
                    }}
                  >
                    {SPORTS.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Key" hint="How sources refer to it.">
                  <TextInput value={key} required onChange={(e) => setKey(e.target.value)} />
                </Field>
                <Field label="Display name" hint="The CSV column header.">
                  <TextInput value={displayName} required onChange={(e) => setDisplayName(e.target.value)} />
                </Field>
                <Field label="Direction" hint="Nothing in a payload reveals this.">
                  <Select value={direction} onChange={(e) => setDirection(e.target.value as Direction)}>
                    <option value="higher_is_better">Higher is better</option>
                    <option value="lower_is_better">Lower is better</option>
                  </Select>
                </Field>
                {/*
                  Beside Direction because they are the same kind of decision
                  about the statistic — which way it runs, and how much it counts
                  — and because statistics are added here in batches, where a
                  weight that could only be typed on a Fields screen is a weight
                  nobody sets.

                  **It is a default, not the ranking.** The ranker reads
                  `Binding.weight`; this is what a *new* Binding starts from. So
                  filing one here
                  cannot re-rank a profile that already exists, which is the
                  measured objection ADR-0040 raised against putting a weight on
                  the entry at all.
                */}
                <Field
                  label="Weight %"
                  hint="Optional. What a new Binding starts from."
                  error={weightProblem}
                >
                  <TextInput
                    value={weight}
                    inputMode="decimal"
                    placeholder="Blank counts as 1"
                    aria-invalid={weightProblem ? true : undefined}
                    onChange={(e) => {
                      setWeight(e.target.value);
                      setWeightProblem(null);
                    }}
                  />
                </Field>
                <Field label="Aliases" hint="Comma separated. Other names sources use.">
                  <TextInput value={aliases} onChange={(e) => setAliases(e.target.value)} />
                </Field>
                {/*
                  **On this form it changes nothing that exists, and that is
                  why the hint does not warn.** A statistic being added has no
                  Bindings, so a declaration made here reaches no published file
                  until some source binds the statistic later. The *editor's*
                  copy of this control is the one that re-ranks sources already
                  reviewed, and its hint says so; promising the same thing here
                  would be a warning over an empty consequence, which is how a
                  reader learns to skip the warning that matters.

                  Asked at the point the statistic is being invented, for the
                  reason the Weight is: statistics are typed in here in batches,
                  and a declaration only reachable from a row editor is one
                  nobody makes.
                */}
                <FieldSet
                  label="Positions Groups Excluded"
                  // Behind the label rather than under it. This says what the
                  // control is for, which is wanted once — and six fields each
                  // carrying a permanent paragraph is a form that reads as
                  // documentation. Safe to hide here because it warns of
                  // nothing: a statistic being *added* has no Bindings, so a
                  // declaration made on this form re-ranks nobody. The editor's
                  // copy of this control, which does, keeps its hint on screen.
                  tip="Optional. These Canonical Position Groups are not ranked on this statistic. A group is the publisher's own labels collected under one name, so adding one is adding a mapping."
                  className="fold:col-span-2"
                >
                  <PositionGroupPicker
                    sport={sport}
                    options={optionsFor(positionGroups, sport, excluded)}
                    labels={labelsFor(positionGroups, sport)}
                    value={excluded}
                    onChange={setExcluded}
                  />
                  {/* Only where something is selected: with none, the control
                      itself already reads "No exclusions". */}
                  {exclusionHint(excluded) && (
                    <span className="mt-xxs block text-caption text-muted-soft">
                      {exclusionHint(excluded)}
                    </span>
                  )}
                </FieldSet>
              </div>
              <div>
                <Button
                  type="submit"
                  variant="primary"
                  icon={<PlusIcon size={16} />}
                  disabled={create.isPending}
                >
                  {create.isPending ? "Adding…" : "Add statistic"}
                </Button>
              </div>
            </div>
          </form>
        </Card>

        {entries.length === 0 ? (
          <EmptyState icon={<DictionaryIcon size={20} />} title="The dictionary is empty">
            Install a vocabulary above, or add statistics one at a time. Either way they bind
            automatically the next time the portal discovers a source publishing them.
          </EmptyState>
        ) : (
          <Card
            title="Statistics"
            description={
              shown.length === entries.length
                ? `${entries.length} recorded`
                : `Showing ${shown.length} of ${entries.length}`
            }
            flush
          >
            <div className="flex flex-col gap-sm border-b border-hairline-soft bg-surface-soft px-lg py-sm fold:flex-row fold:items-center">
              <div className="relative fold:max-w-[22rem] fold:flex-1">
                <SearchIcon
                  size={16}
                  className="pointer-events-none absolute left-[12px] top-1/2 -translate-y-1/2 text-muted"
                />
                <TextInputCompact
                  role="searchbox"
                  className="pl-[36px] pr-[36px]"
                  placeholder="Search key, name or alias"
                  aria-label="Search statistics"
                  value={view.query}
                  onChange={(e) => narrow({ query: e.target.value })}
                />
                {view.query !== "" && (
                  <button
                    type="button"
                    aria-label="Clear search"
                    className="absolute right-[8px] top-1/2 -translate-y-1/2 rounded-sm p-[4px] text-muted hover:text-ink"
                    onClick={() => narrow({ query: "" })}
                  >
                    <CloseIcon size={14} />
                  </button>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-xs">
                {(sports ?? []).length > 1 && (
                  <SelectCompact
                    aria-label="Sport"
                    className="w-auto"
                    value={view.sport}
                    onChange={(e) => narrow({ sport: e.target.value })}
                  >
                    <option value="">All sports</option>
                    {(sports ?? []).map((name) => (
                      <option key={name} value={name}>
                        {name.charAt(0).toUpperCase() + name.slice(1)}
                      </option>
                    ))}
                  </SelectCompact>
                )}
                <SelectCompact
                  aria-label="Direction"
                  className="w-auto"
                  value={view.direction}
                  onChange={(e) => narrow({ direction: e.target.value as StatView["direction"] })}
                >
                  <option value="">Any direction</option>
                  <option value="higher_is_better">Higher is better</option>
                  <option value="lower_is_better">Lower is better</option>
                </SelectCompact>
                <SelectCompact
                  aria-label="Weight"
                  className="w-auto"
                  value={view.weight}
                  onChange={(e) => narrow({ weight: e.target.value as StatView["weight"] })}
                >
                  <option value="">Any weight</option>
                  <option value="declared">Has a weight</option>
                  <option value="undeclared">No weight</option>
                </SelectCompact>
                {isNarrowed(view) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<CloseIcon size={14} />}
                    onClick={() => setView({ ...DEFAULT_VIEW, sort: view.sort })}
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            </div>
            {shown.length === 0 && (
              <div className="p-md">
                <EmptyState
                  icon={<DictionaryIcon size={20} />}
                  title="No statistics match"
                  action={
                    <Button variant="secondary" onClick={() => setView({ ...DEFAULT_VIEW, sort: view.sort })}>
                      Clear search and filters
                    </Button>
                  }
                >
                  Nothing in the dictionary matches what is searched and filtered above.
                </EmptyState>
              </div>
            )}
            <div
              hidden={shown.length === 0}
              className={settling ? "opacity-60 transition-opacity" : "transition-opacity"}
            >
              <TableScroll stickyHeader>
                <table className="table">
                  <thead>
                    <tr>
                      {sortable("sport", "Sport")}
                      {sortable("key", "Key")}
                      {sortable("display_name", "Display name")}
                      {sortable("direction", "Direction")}
                      {sortable("weight", "Weight %")}
                      {/*
                        **The client's own name for the column**, not a shorter
                        one: they asked for "Positions Groups Excluded" and that
                        is what the three controls are captioned. `CONTEXT.md`
                        keeps "excluded positions" off the *prose* for this —
                        the thing excluded is a Canonical Position Group — and a
                        caption the client chose is a name rather than prose.

                        Not sortable, like Aliases: the cell holds a list, and
                        there is no order over "QB, OL" and "DB" that anybody
                        would be asking for.
                      */}
                      <th>Positions Groups Excluded</th>
                      <th>Aliases</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {ordered.map((entry) => (
                      <StatRow
                        key={entry.id}
                        entry={entry}
                        hidden={!visible.has(entry.id)}
                        last={entry.id === shown.at(-1)?.id}
                        onDirection={onDirection}
                        onWeight={onWeight}
                        onExclusions={onExclusions}
                        positionGroups={positionGroups}
                        onEdit={setEditing}
                        onRemove={onRemove}
                      />
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            </div>
          </Card>
        )}

        {/*
          Keyed on the row, so switching rows remounts the form rather than
          carrying one entry's half-typed key into another's boxes.

          Held as an id rather than as the entry so that a row deleted from
          another tab closes the editor instead of leaving a form over a
          statistic that is gone. The *contents* do not follow a refetch — the
          modal snapshots the row at open and diffs against that snapshot, so
          that saving one field cannot write back stale values for the others
          (see `original`).
        */}
        {editing && entries.some((entry) => entry.id === editing) && (
          <EditStatModal
            key={editing}
            entry={entries.find((entry) => entry.id === editing)!}
            onClose={() => setEditing(null)}
            onSaved={async () => {
              setEditing(null);
              await invalidate();
            }}
          />
        )}
      </div>
    </Layout>
  );
}


/**
 * One entry, edited whole.
 *
 * **A form rather than more in-place cells, and it saves in one request.** The
 * two cells this screen already had are each one deliberate act — a Direction
 * from a select, a Weight committed on blur — and that works for two fields.
 * Ten of them in a row is a row that saves ten times on the way to being read,
 * and a key saved per keystroke would run the server's rename cascade over
 * every prefix of what was being typed.
 *
 * **The key is why this is a modal and not an expanding row.** Changing it
 * carries every Binding, every eligibility threshold and every per-90
 * derivation in the sport across with it, and what that costs has to be read
 * before the button is pressed. A warning that needs scrolling to is a warning
 * nobody read.
 */
function EditStatModal({
  entry,
  onClose,
  onSaved,
}: {
  entry: StatEntry;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  /**
   * The row as it was when the editor opened, and what the draft is diffed
   * against.
   *
   * **Not the live `entry` prop**, which keeps refetching underneath — the list
   * query refetches on window focus. Diffing a draft frozen at open against a
   * row that has since moved would send back the *old* value of every field
   * somebody else corrected in the meantime: a silent revert of another
   * person's edit, performed by an operator who only came to fix a key.
   *
   * Against the opening snapshot, a field this editor did not touch is simply
   * not in the patch, so a concurrent correction to it survives untouched. The
   * row on screen may be a few seconds stale; the write is right.
   */
  const [original] = useState<StatEntry>(entry);
  const [draft, setDraft] = useState<StatDraft>(() => draftFor(entry));
  /** Which box the last refusal named, and what it said. */
  const [problem, setProblem] = useState<{ field: string; message: string } | null>(null);

  const save = useMutation({
    mutationFn: (patch: StatPatch) => api.updateStat(entry.id, patch),
    onSuccess: () => onSaved(),
    onError: (error) => {
      if (error instanceof ApiError && error.field) {
        setProblem({ field: error.field, message: error.message });
      }
    },
  });

  const set = <K extends keyof StatDraft>(field: K, value: StatDraft[K]) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setProblem(null);
    // **The mutation's own error has to go too.** `problem` is what puts a
    // message against a box; `save.error` is what puts one in the banner, and
    // they are set from the same refusal. Clearing only the first moves a 409
    // from the Key box to a danger banner on the next keystroke, where it reads
    // as a second, new failure.
    save.reset();
  };

  /**
   * What a rename would carry, fetched when the editor opens rather than when
   * the key is first touched.
   *
   * Earlier on purpose: the figure that matters is how many *scheduled* sources
   * move, and somebody deciding whether to rename at all should be able to read
   * that before they have committed to typing. One request per open, against an
   * endpoint that counts rows.
   */
  const { data: usage, isPending: usagePending, error: usageError } = useQuery({
    queryKey: ["stat-usage", entry.id],
    queryFn: () => api.statUsage(entry.id),
  });

  /**
   * The position groups this entry's sport offers.
   *
   * Asked for here rather than handed down from the table, because the key is
   * shared: the list is already in the cache by the time a row can be opened,
   * so this is a read rather than a request, and the modal does not have to be
   * given a prop for every lookup it makes.
   */
  const { data: positionGroups } = useQuery({
    queryKey: keys.positionGroups,
    queryFn: api.listPositionGroups,
  });

  /**
   * Refused in the form before the mutation, never inside it.
   *
   * A client-side refusal is not a failed request and must not be reported as
   * one: raising it through the mutation would leave `save.error` set, and the
   * banner that reads "could not save" would appear *beside* the box that
   * already says what is wrong — two messages for one mistake, one of them
   * blaming the server for a value it never saw.
   */
  const submit = () => {
    const result = changesFor(original, draft);
    if (!result.ok) {
      setProblem({ field: result.field, message: result.message });
      return;
    }
    // A form that moved nothing is closed rather than saved. Without this,
    // opening an entry and pressing Save would `PATCH` an empty body — and on
    // an untouched key, would ask the cascade to move it to where it is.
    if (Object.keys(result.patch).length === 0) {
      onClose();
      return;
    }
    save.mutate(result.patch);
  };

  const renamed = draft.key.trim() !== original.key;
  const summary = usage ? renameSummary(usage) : null;
  const scheduled = (usage?.profiles ?? []).filter((profile) => profile.schedule_enabled);
  const errorFor = (field: string) => (problem?.field === field ? problem.message : null);

  /**
   * The boxes this form actually renders, and therefore the only `field` values
   * a refusal can be *shown against*.
   *
   * **Tested against this list rather than against whether `problem` is set.**
   * `onError` fills `problem` from the error's own field, so asking "did
   * `errorFor` find it?" always answers yes and the banner never fires — which
   * means a refusal naming a field with no box, like `per_90_of` on a 400
   * `per_90_self_reference`, is rendered nowhere at all and Save appears to do
   * nothing.
   */
  const SHOWN_FIELDS = [
    "key",
    "display_name",
    "direction",
    "default_weight",
    "aliases",
    "description",
  ];
  const unplaced =
    save.error instanceof ApiError
      ? SHOWN_FIELDS.includes(save.error.field ?? "")
        ? null
        : save.error.message
      : save.error
        ? "Could not save that statistic."
        : null;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit ${original.display_name}`}
      description="Direction and the aliases are properties of the statistic, so correcting one here corrects every source in this sport at once."
      width="max-w-[44rem]"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={save.isPending} onClick={submit}>
            {save.isPending ? "Saving…" : "Save changes"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-md">
        {unplaced && <Banner tone="danger" title={unplaced} />}

        {/*
          Shown only once the key has actually been edited. Shown on open it
          would be a warning about something nobody is doing, which is how a
          reader learns to skip the banner that matters.
        */}
        {/*
          **A failed or pending usage read still shows a banner.** Rendering the
          warning only when `usage` resolved means a `/usage` request that 404s
          or times out removes it entirely — and "no warning" is indistinguishable
          from "nothing is bound to this", which is the one reading that would
          make somebody press Save. Not knowing is said out loud instead.
        */}
        {renamed && (usagePending || usageError) && (
          <Banner
            tone="warning"
            title={
              usagePending
                ? "Checking what this rename would move…"
                : "Could not check what this rename would move"
            }
          >
            <p className="m-0">
              Every Binding, eligibility threshold and per-90 derivation naming this key in{" "}
              {original.sport} is carried across with it, and published columns are ordered by
              key — so any file bound to it keeps every header and value and publishes them in a
              different order. How many sources that is could not be read.
            </p>
          </Banner>
        )}
        {renamed && summary && (
          <Banner tone="warning" title="Renaming this key moves every source bound to it">
            <p className="m-0">{summary}</p>
            {scheduled.length > 0 && (
              <p className="mb-0 mt-xs">
                On a timer:{" "}
                {scheduled.map((profile) => `${profile.league} ${profile.season}`).join(", ")}.
              </p>
            )}
          </Banner>
        )}

        <div className="grid grid-cols-1 gap-md fold:grid-cols-2">
          {/*
            Shown and not editable, which is deliberate rather than an omission.
            An entry is identified by sport and key together, and only the key
            can be corrected: a football Binding does not become a soccer
            Binding, so moving the entry between sports would leave every
            Binding naming a key with no entry in its own sport and nothing for
            the rename to carry. Delete and re-add is the honest route, and it
            makes the Bindings visible as work.
          */}
          <FieldSet label="Sport" hint="Part of what identifies the entry, so it cannot be moved. Delete and re-add to change it.">
            <Badge>{original.sport}</Badge>
          </FieldSet>
          <Field
            label="Key"
            hint="How sources refer to it. Changing it carries every source across."
            error={errorFor("key")}
          >
            <TextInput
              value={draft.key}
              aria-invalid={errorFor("key") ? true : undefined}
              onChange={(e) => set("key", e.target.value)}
            />
          </Field>
          <Field
            label="Display name"
            hint="The CSV column header, rewritten on the next run of every source bound to it."
            error={errorFor("display_name")}
          >
            <TextInput
              value={draft.display_name}
              aria-invalid={errorFor("display_name") ? true : undefined}
              onChange={(e) => set("display_name", e.target.value)}
            />
          </Field>
          <Field label="Direction" hint="Nothing in a payload reveals this.">
            <Select
              value={draft.direction}
              onChange={(e) => set("direction", e.target.value as Direction)}
            >
              <option value="higher_is_better">Higher is better</option>
              <option value="lower_is_better">Lower is better</option>
            </Select>
          </Field>
          {/*
            **A Weight edited here changes nothing that already exists**, which
            the screen's own description already says and this hint repeats where
            the decision is made. The ranker reads `Binding.weight`; this is only
            what a *new* Binding starts from, which is what keeps the field clear
            of ADR-0040.
          */}
          <Field
            label="Weight %"
            hint="What a new Binding starts from. Blank counts as 1 and re-ranks nothing already reviewed."
            error={errorFor("default_weight")}
          >
            <TextInput
              value={draft.weight}
              inputMode="decimal"
              placeholder="Blank counts as 1"
              aria-invalid={errorFor("default_weight") ? true : undefined}
              onChange={(e) => set("weight", e.target.value)}
            />
          </Field>
          <Field
            label="Aliases"
            hint="Comma separated. Path fragments that auto-bind during discovery."
            error={errorFor("aliases")}
            className="fold:col-span-2"
          >
            <TextInput value={draft.aliases} onChange={(e) => set("aliases", e.target.value)} />
          </Field>
          {/*
            **This one re-ranks sources that already exist**, which is the
            opposite of the Weight above it and is why the hint says so. The
            ranker reads the exclusions off the *entry* — like the Direction, and
            unlike `default_weight`, which only seeds a new Binding — so a group
            added here is a group every source in the sport stops ranking on this
            statistic at its next firing.
          */}
          {/*
            **This one keeps its hint on screen**, unlike the add form's copy of
            the same control, and the asymmetry is the point rather than an
            oversight. Saving here re-ranks every source already bound to the
            statistic, on its next firing — the same as correcting a Direction —
            and a consequence behind a hover is a consequence for whoever
            happened to hover. The `tip` carries the explanation; the warning
            stays where it cannot be missed.
          */}
          <FieldSet
            label="Positions Groups Excluded"
            tip="A group is the publisher's own labels collected under one name. A player in one of these takes no percentile for this statistic, and the statistic leaves his Impact Factor rather than scoring him at the midpoint."
            hint="Read by every source in this sport, on its next run."
            className="fold:col-span-2"
          >
            <PositionGroupPicker
              sport={original.sport}
              options={optionsFor(positionGroups, original.sport, draft.excluded)}
              labels={labelsFor(positionGroups, original.sport)}
              value={draft.excluded}
              onChange={(groups) => set("excluded", groups)}
            />
            {exclusionHint(draft.excluded) && (
              <span className="mt-xxs block text-caption text-muted-soft">
                {exclusionHint(draft.excluded)}
              </span>
            )}
          </FieldSet>
          <Field label="Description" hint="Optional. What the number means." className="fold:col-span-2">
            <TextArea
              rows={2}
              value={draft.description}
              onChange={(e) => set("description", e.target.value)}
            />
          </Field>
        </div>

        {/*
          What the entry declares that this form does not edit, shown rather
          than hidden. Both arrive with a Vocabulary — soccer's ninety-one
          declarations are not typed one at a time — and a form that silently
          omitted them would read as an entry that declares nothing.
        */}
        {(original.distribution || original.per_90_of) && (
          <Muted>
            {original.distribution && <>Distribution: {original.distribution}. </>}
            {original.per_90_of && (
              <>
                Derived as {original.per_90_of} per ninety minutes of {original.per_90_minutes_key}.{" "}
              </>
            )}
            Both are installed with a Vocabulary rather than typed here.
          </Muted>
        )}
      </div>
    </Modal>
  );
}


/**
 * One entry's excluded Canonical Position Groups, edited in place.
 *
 * **In the cell because the client asked for the dropdown to be in the column**
 * — "this dropdown shows a list of all available positions… select one or more"
 * — and because the two cells beside it, Direction and Weight, are already
 * editable there. Reaching the row editor to change it would have made this the
 * only read-only control in a row of live ones.
 *
 * **One request per visit, not one per tick.** This is `DefaultWeightCell`'s
 * discipline in the other shape: that one commits on blur rather than on a
 * keystroke, because four keystrokes to type "37.5" would `PATCH` four times and
 * the third sends 37. Three ticks here would be three writes to the column that
 * decides which positions a statistic ranks — and the intermediate ones would be
 * read by every source in the sport on their next firing. So the panel holds a
 * draft and the close saves it.
 *
 * A visit that changed nothing writes nothing, which is what keeps tabbing
 * through the table from `PATCH`ing every row it passes.
 */
function ExclusionsCell({
  entry,
  options,
  labels,
  onChange,
}: {
  entry: StatEntry;
  options: readonly string[];
  /** What feeds each group, passed straight through to the picker. */
  labels?: Readonly<Record<string, readonly string[]>>;
  onChange: (next: string[]) => void;
}) {
  const stored = entry.excluded_position_groups;
  const [draft, setDraft] = useState<string[]>(stored);

  // Re-seed when the row changes underneath the control — another tab, or this
  // screen's own refetch after a save. Keyed on the groups' *contents*, not on
  // the array: `stored` is a fresh identity on every refetch, so keying on it
  // would reset a selection somebody is still making every time the list query
  // refetched on window focus.
  //
  // A comma is a safe separator because a group name cannot contain one — the
  // server only ever returns a name from the sport's own vocabulary.
  useEffect(() => {
    setDraft(stored);
  }, [stored.join(",")]);

  return (
    <PositionGroupPicker
      compact
      sport={entry.sport}
      options={options}
      labels={labels}
      value={draft}
      label={`Positions Groups Excluded for ${entry.display_name}`}
      onChange={setDraft}
      onCommit={(next) => {
        // The groups come from the control rather than from `draft`, which may
        // be a render behind it: adding a group selects and saves in one act.
        setDraft(next);
        if (!sameGroups(next, stored)) onChange(next);
      }}
    />
  );
}

/**
 * One entry's default Weight, edited in place.
 *
 * **Commits on blur and on Enter, never on a keystroke.** Every other control in
 * this table is a `select`, where one change is one deliberate act and one
 * request. A text box is not: saving per keystroke would `PATCH` four times on
 * the way to typing "37.5", and the third of those sends 37 — a value nobody
 * meant, written to a row every profile in the sport reads.
 *
 * A refused value is shown with its reason and not sent. Leaving the box with
 * something unparseable in it therefore changes nothing, which is the behaviour
 * that matches "it was refused" — and the message stays until it is edited.
 */
function DefaultWeightCell({
  entry,
  onChange,
}: {
  entry: StatEntry;
  onChange: (next: number | null) => void;
}) {
  const stored = entry.default_weight;
  const [text, setText] = useState(stored == null ? "" : String(stored));
  const [problem, setProblem] = useState<string | null>(null);

  // Re-seed when the row changes underneath the box — another tab, or this
  // screen's own refetch after a save. Guarded on the value differing from what
  // is displayed so a save does not wipe what is being typed next to it.
  useEffect(() => {
    setText(stored == null ? "" : String(stored));
    setProblem(null);
  }, [stored]);

  function commit() {
    const parsed = parseWeightPercent(text);
    if (!parsed.ok) {
      setProblem(parsed.message);
      return;
    }
    setProblem(null);
    // Nothing to do where the value did not move. Without this, tabbing through
    // the table would `PATCH` every row it passed.
    if (parsed.weight === stored) return;
    onChange(parsed.weight);
  }

  return (
    <div className="flex flex-col gap-xxs">
      <TextInputCompact
        aria-label={`Default weight percentage for ${entry.display_name}`}
        width="w-[76px]"
        inputMode="decimal"
        placeholder="1"
        aria-invalid={problem ? true : undefined}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setProblem(null);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          }
        }}
      />
      {problem && <span className="text-micro text-danger-ink">{problem}</span>}
    </div>
  );
}

/** The human message an API rejection carries, or a fallback for anything else. */
function messageFor(error: unknown, fallback: string): string | null {
  if (!error) return null;
  return error instanceof ApiError ? error.message : fallback;
}

/**
 * One row of the dictionary table, memoised.
 *
 * The table renders every entry — 488 on the live database — and hides the
 * ones a search or filter leaves out rather than unmounting them. That is what
 * keeps "Clear" quick: un-hiding a row is an attribute, while remounting it
 * rebuilds its Direction menu and Weight box. Memoised so a keystroke in the
 * search box re-renders only the rows whose visibility it changed; every
 * callback handed in is stable for the same reason.
 */
const StatRow = memo(function StatRow({
  entry,
  hidden,
  last,
  onDirection,
  onWeight,
  onExclusions,
  positionGroups,
  onEdit,
  onRemove,
}: {
  entry: StatEntry;
  hidden: boolean;
  /** The last row shown, which drops its bottom rule the way a last child does —
   *  hidden rows after it mean the stylesheet's `:last-child` misses it. */
  last: boolean;
  onDirection: (entry: StatEntry, next: Direction) => void;
  onWeight: (entry: StatEntry, next: number | null) => void;
  onExclusions: (entry: StatEntry, next: string[]) => void;
  /** Every sport's groups, in one object, so this is a stable prop across the
   *  488 rows the memo exists to keep from re-rendering. */
  positionGroups: PositionGroups | undefined;
  onEdit: (id: string) => void;
  onRemove: (entry: StatEntry) => void;
}) {
  return (
    <tr hidden={hidden} className={last ? "[&>td]:border-b-0" : undefined}>
      <td>
        <Badge>{entry.sport}</Badge>
      </td>
      {/* Eleven characters a line. `.code` breaks anywhere, which let the table
          squeeze this column to seven and hand the room to Aliases. */}
      <td>
        <Code className="inline-block w-[11ch]">{entry.key}</Code>
      </td>
      <td>{entry.display_name}</td>
      <td>
        <SelectCompact
          value={entry.direction}
          onChange={(e) =>
            onDirection(entry, e.target.value as Direction)
          }
        >
          <option value="higher_is_better">Higher is better</option>
          <option value="lower_is_better">Lower is better</option>
        </SelectCompact>
      </td>
      <td>
        <DefaultWeightCell
          entry={entry}
          onChange={(next) => onWeight(entry, next)}
        />
      </td>
      {/*
        Editable here, beside the Direction and the Weight, because the client
        asked for the dropdown to be in the column — and because a read-only
        control in a row of live ones is the odd one out. It shuts and saves
        once; `ExclusionsCell` has the argument.

        The shut control reads "No exclusions" rather than blank, which is the
        client's own wording: a blank cell on this column would read as a row
        nobody has filled in yet.
      */}
      <td>
        <span className="inline-block w-[150px] align-middle">
          <ExclusionsCell
            entry={entry}
            options={optionsFor(positionGroups, entry.sport, entry.excluded_position_groups)}
            labels={labelsFor(positionGroups, entry.sport)}
            onChange={(next) => onExclusions(entry, next)}
          />
        </span>
      </td>
      <td>
        {entry.aliases.length > 0 ? (
          // Capped so Aliases stops taking the room the columns before it need.
          // A badge longer than the cap wraps inside itself rather than spilling.
          <span className="inline-flex max-w-[175px] flex-wrap gap-xxs [&>span]:max-w-full [&>span]:break-all">
            {entry.aliases.map((alias) => (
              <Badge key={alias}>{alias}</Badge>
            ))}
          </span>
        ) : (
          <Muted>—</Muted>
        )}
      </td>
      <td className="text-right">
        <span className="inline-flex gap-xs">
          {/*
            Beside Remove, and before it: everything this row
            shows read-only — the key, the display name, the
            aliases — is corrected through here, and until now
            the only way to fix a mistyped key was to delete the
            entry and lose every Binding made from it.
          */}
          <Button
            variant="secondary"
            size="sm"
            icon={<PencilIcon size={14} />}
            onClick={() => onEdit(entry.id)}
          >
            Edit
          </Button>
          <Button
            variant="danger"
            size="sm"
            icon={<TrashIcon size={14} />}
            onClick={() => onRemove(entry)}
          >
            Remove
          </Button>
        </span>
      </td>
    </tr>
  );
});

